import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/axios";
import AdminShell from "./AdminShell";
import GpsManagementTabs from "./GpsManagementTabs";
import { NOT_AVAILABLE } from "./gpsManagementConstants";
import {
  boundaryDisplay, buildOperatorTimeline, coverageExplanation, tripStatusDisplay,
} from "../utils/gpsTripPresentation";
import "../styles/admin.css";

const MAX_TRIP_RANGE_MS = 24 * 60 * 60 * 1000;
const TRIP_PAGE_SIZE = 20;

function localInputValue(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function formatTime(value) {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : NOT_AVAILABLE;
}

function formatDuration(durationMs) {
  const parsed = Number(durationMs);
  if (!Number.isFinite(parsed)) return NOT_AVAILABLE;
  const minutes = Math.max(0, Math.round(parsed / 60_000));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return hours ? `${hours}h ${remainder}m` : `${minutes}m`;
}

function formatClock(value) {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : NOT_AVAILABLE;
}

function titleCase(value) {
  return String(value || "").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function TripCard({ trip, onReplayTrip }) {
  const timeline = buildOperatorTimeline(trip);
  const coverage = trip.coverage?.classification || "poor";
  return <article className="gps-trip-card">
    <header>
      <div className="gps-trip-title">
        <span>Trip time</span>
        <h2>{formatClock(trip.startAt)} → {formatClock(trip.endAt)}</h2>
        <p>{formatDuration(trip.durationMs)} total · {formatDuration(trip.drivingDurationMs)} observed driving</p>
      </div>
      <div className="gps-trip-badges">
        <strong className={`gps-trip-status gps-trip-status--${trip.status || "completed"}`}>
          {tripStatusDisplay(trip.status)}
        </strong>
        <strong className={`gps-trip-coverage gps-trip-coverage--${coverage}`}>
          Coverage: {titleCase(coverage)}
        </strong>
      </div>
    </header>
    <div className="gps-trip-metrics">
      <div className="gps-trip-metric--primary"><span>Observed GPS distance</span><strong>{(trip.observedGpsDistanceMeters / 1000).toFixed(2)} km</strong></div>
      <div><span>Average moving speed</span><strong>{Number(trip.averageMovingSpeedKph).toFixed(1)} km/h</strong></div>
      <div><span>Maximum speed</span><strong>{Number(trip.maximumReportedSpeedKph).toFixed(1)} km/h</strong></div>
      <div><span>Stops</span><strong>{trip.stopCount}</strong></div>
      <div><span>GPS movement gaps</span><strong>{trip.movementGapCount}</strong></div>
      <div><span>Unknown travel</span><strong>{formatDuration(trip.coverage?.unknownDurationMs)}</strong></div>
    </div>
    <div className={`gps-trip-explanation gps-trip-explanation--${coverage}`}>
      <strong>{coverageExplanation(trip)}</strong>
      {Number(trip.coverage?.unknownDurationMs) > 0 ? <span>
        Unknown travel is time between GPS observations where the exact traveled path is unknown. No distance is calculated across those intervals.
      </span> : null}
    </div>
    <div className="gps-trip-actions">
      <span>{boundaryDisplay(trip.boundaryConfidence, trip.status)}</span>
      <button type="button" onClick={() => onReplayTrip({
        vehicleId: trip.vehicleId,
        from: trip.startAt,
        to: trip.endAt,
        trip: {
          id: trip.id,
          coverage,
          startAt: trip.startAt,
          endAt: trip.endAt,
        },
      })}>Replay trip</button>
    </div>
    <div className="gps-trip-timeline" aria-label={`Activity for ${trip.id}`}>
      <h3>Activity timeline</h3>
      {timeline.map((activity, index) => <div key={`${activity.at}-${activity.type}-${index}`}>
        <time>{formatTime(activity.at)}</time>
        <span><strong>{activity.label}</strong>
          {activity.stop ? <small>
            {formatDuration(activity.stop.durationMs)} · ended {formatClock(activity.stop.endAt)} · {activity.stop.boundary ? "Trip-ending parked period" : "During trip"}
          </small> : null}
          {activity.detail ? <small>{activity.detail}</small> : null}
        </span>
      </div>)}
    </div>
  </article>;
}

export default function GpsTripsActivity({
  activeView = "trips",
  onViewChange = () => {},
  onReplayTrip = () => {},
}) {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [from, setFrom] = useState(() => localInputValue(new Date(Date.now() - MAX_TRIP_RANGE_MS)));
  const [to, setTo] = useState(() => localInputValue(new Date()));
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestAbort = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    api.get("/gps/fleet/live", {
      params: { page: 1, limit: 1000, source: "physical" }, signal: controller.signal,
    }).then((response) => {
      const physical = (response.data?.items || []).filter((item) => item.source === "physical");
      setVehicles(physical);
      setVehicleId((current) => current || physical[0]?.vehicle?.id || "");
    }).catch((caught) => {
      if (caught?.code !== "ERR_CANCELED") setError("Physical vehicles could not be loaded.");
    });
    return () => controller.abort();
  }, []);

  useEffect(() => () => requestAbort.current?.abort(), []);

  const loadTrips = useCallback(async (requestedPage = 1) => {
    const rangeFrom = new Date(from);
    const rangeTo = new Date(to);
    if (!vehicleId || !Number.isFinite(rangeFrom.getTime()) || !Number.isFinite(rangeTo.getTime()) ||
        rangeFrom >= rangeTo || rangeTo - rangeFrom > MAX_TRIP_RANGE_MS) {
      setError("Choose a physical vehicle and a valid range of no more than 24 hours.");
      return;
    }
    requestAbort.current?.abort();
    const controller = new AbortController();
    requestAbort.current = controller;
    setLoading(true);
    setError("");
    try {
      const response = await api.get(`/gps/vehicles/${vehicleId}/trips`, {
        params: {
          from: rangeFrom.toISOString(), to: rangeTo.toISOString(),
          page: requestedPage, limit: TRIP_PAGE_SIZE,
        },
        signal: controller.signal,
      });
      if (response.data?.source !== "physical" || !Array.isArray(response.data?.trips)) {
        throw new Error("invalid trip response");
      }
      setResult(response.data);
      setPage(requestedPage);
    } catch (caught) {
      if (caught?.code !== "ERR_CANCELED" && caught?.name !== "CanceledError") {
        setResult(null);
        setError(caught?.response?.data?.code === "GPS_TRIP_PROJECTION_TOO_DENSE"
          ? "This range contains too many observations to project safely. Choose a shorter range."
          : "Trips and activity could not be loaded.");
      }
    } finally {
      if (requestAbort.current === controller) requestAbort.current = null;
      setLoading(false);
    }
  }, [from, to, vehicleId]);

  const selectedVehicle = useMemo(
    () => vehicles.find((item) => item.vehicle?.id === vehicleId), [vehicleId, vehicles]
  );
  const trips = result?.trips || [];

  return (
    <AdminShell pageClassName="admin-shell--live-monitoring" eyebrow="FLEET ACTIVITY" title="Trips & Activity"
      subtitle="Conservative trip and stop intelligence from physical GPS observations.">
      <div className="gps-trips-dashboard">
        <GpsManagementTabs activeView={activeView} onViewChange={onViewChange} />
        <section className="gps-replay-query" aria-label="Trip activity range">
          <label>Physical vehicle<select aria-label="Trip vehicle" value={vehicleId}
            onChange={(event) => { setVehicleId(event.target.value); setResult(null); setPage(1); }}>
            <option value="">Select a vehicle</option>
            {vehicles.map((item) => <option key={item.vehicle.id} value={item.vehicle.id}>
              {item.vehicle.plateNumber} — {[item.vehicle.make, item.vehicle.model].filter(Boolean).join(" ")}
            </option>)}
          </select></label>
          <label>From<input aria-label="Trips from" type="datetime-local" value={from}
            onChange={(event) => setFrom(event.target.value)} /></label>
          <label>To<input aria-label="Trips to" type="datetime-local" value={to}
            onChange={(event) => setTo(event.target.value)} /></label>
          <button type="button" disabled={loading || !vehicleId} onClick={() => loadTrips(1)}>
            {loading ? "Projecting activity..." : "Load trips"}
          </button>
        </section>
        {error ? <div className="gps-replay-state gps-replay-state--error" role="alert">{error}</div> : null}
        {result ? <section className="gps-trips-summary" aria-label="Trip projection summary">
          <div><span>Vehicle</span><strong>{selectedVehicle?.vehicle?.plateNumber || NOT_AVAILABLE}</strong></div>
          <div><span>Projected trips</span><strong>{result.pagination?.total ?? trips.length}</strong></div>
          <div><span>Physical observations</span><strong>{result.observationCount ?? NOT_AVAILABLE}</strong></div>
          <div><span>Selected period</span><strong>{formatDuration(new Date(result.range?.to) - new Date(result.range?.from))}</strong></div>
        </section> : null}
        {result && trips.length === 0 ? <div className="gps-replay-state">
          No movement met the conservative trip-confirmation policy in this range.
        </div> : null}
        <section className="gps-trip-list" aria-label="Projected trips">
          {trips.map((trip) => <TripCard key={trip.id} trip={trip} onReplayTrip={onReplayTrip} />)}
        </section>
        {result && result.pagination?.pages > 1 ? <div className="gps-trips-pager">
          <button type="button" disabled={loading || page <= 1} onClick={() => loadTrips(page - 1)}>Previous</button>
          <span>Page {page} of {result.pagination.pages}</span>
          <button type="button" disabled={loading || page >= result.pagination.pages} onClick={() => loadTrips(page + 1)}>Next</button>
        </div> : null}
      </div>
    </AdminShell>
  );
}
